import { Table } from "@/components/elements/Table";
import { DATE_FORMAT } from "@/constants/datetime";
import { STATUS_LABEL } from "@/constants/enum";
import { EMPTY_STR } from "@/constants/string";
import { dayjs } from "@/utils/dayjs";
import { TLearningContentCategory } from "@/features/learning-content/types/learningContent";
import { useTable } from "@/hooks/useTable";
import { AppRoutes } from "@/routes/config";
import { state } from "@/theme/colors";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Tooltip, Typography } from "@mui/material";
import { MRT_ColumnDef, MRT_Row } from "material-react-table";
import { FC, useMemo } from "react";
import { useNavigate } from "react-router-dom";

type TLearningContentTableProps = {
  isLoading: boolean;
  isFetching?: boolean;
  rows: TLearningContentCategory[];
  onDragEnd: (draggedRow: MRT_Row<TLearningContentCategory>, targetRow: MRT_Row<TLearningContentCategory> | null) => void;
};

export const LearningContentTable: FC<TLearningContentTableProps> = ({ isLoading, isFetching, rows, onDragEnd }) => {
  const navigate = useNavigate();

  const handleRowClick = (row: TLearningContentCategory) => {
    navigate(AppRoutes.industryDetail.replace(":id", row.id.toString()));
  };

  const columns = useMemo<MRT_ColumnDef<TLearningContentCategory>[]>(
    () => [
      {
        header: "状態",
        accessorKey: "isPublished",
        size: 74,
        muiTableHeadCellProps: { sx: { width: "74px" } },
        muiTableBodyCellProps: { sx: { width: "74px" } },
        Cell: ({ row }) => <Typography variant="body14">{row.original.isPublished ? STATUS_LABEL.ENABLED : STATUS_LABEL.PRIVATE}</Typography>,
      },
      {
        header: "分野名",
        accessorKey: "name",
        Header: () => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body14Semi">分野名</Typography>
            <Tooltip title="製造業と建設は、「分野名 - 区分名」" arrow>
              <InfoOutlinedIcon sx={{ fontSize: 20, color: "text.secondary", cursor: "pointer" }} />
            </Tooltip>
          </Box>
        ),
        Cell: ({ row }) => <Typography variant="body14">{row.original.name || EMPTY_STR.TEXT}</Typography>,
      },
      {
        header: "試験種別",
        accessorKey: "examType",
        size: 88,
        muiTableHeadCellProps: { sx: { width: "88px" } },
        muiTableBodyCellProps: { sx: { width: "88px" } },
        Cell: ({ row }) =>
          row.original.examType ? (
            <Typography variant="body14">{row.original.examType}</Typography>
          ) : (
            <Typography variant="body14" sx={{ color: state.error_1 }}>
              (未登録)
            </Typography>
          ),
      },
      {
        header: "WEEK公開",
        accessorKey: "weekPublishStatus",
        size: 100,
        muiTableHeadCellProps: { sx: { width: "100px" } },
        muiTableBodyCellProps: { sx: { width: "100px" } },
        Cell: ({ row }) => {
          const status = row.original.weekPublishStatus;
          if (!status) {
            return (
              <Typography variant="body14" sx={{ color: state.error_1 }}>
                (未作成)
              </Typography>
            );
          }
          return <Typography variant="body14">{status}</Typography>;
        },
      },
      {
        header: "有効数",
        accessorKey: "subjectCount",
        size: 74,
        muiTableHeadCellProps: { sx: { width: "74px" } },
        muiTableBodyCellProps: { sx: { width: "74px" } },
        Cell: ({ row }) => {
          const count = row.original.subjectCount;
          return (
            <Typography variant="body14" sx={{ color: count === 0 ? state.error_1 : undefined }}>
              {count} 週
            </Typography>
          );
        },
      },
      {
        header: "公開数",
        accessorKey: "publishedWeekCount",
        size: 74,
        muiTableHeadCellProps: { sx: { width: "74px" } },
        muiTableBodyCellProps: { sx: { width: "74px" } },
        Cell: ({ row }) => {
          const count = row.original.publishedWeekCount;
          return (
            <Typography variant="body14" sx={{ color: count === 0 ? state.error_1 : undefined }}>
              {count} 週
            </Typography>
          );
        },
      },
      {
        header: "次の試験日",
        accessorKey: "nextExamDate",
        size: 102,
        muiTableHeadCellProps: { sx: { width: "102px" } },
        muiTableBodyCellProps: { sx: { width: "102px" } },
        Cell: ({ row }) => {
          if (!row.original.isRegistered && !row.original.nextExamDate) {
            return (
              <Typography variant="body14" sx={{ color: state.error_1 }}>
                (未登録)
              </Typography>
            );
          }
          return <Typography variant="body14">{row.original.nextExamDate || `予約制`}</Typography>;
        },
      },
      {
        header: "社内メモ",
        accessorKey: "internalNote",
        size: 180,
        muiTableHeadCellProps: { sx: { width: "180px", maxWidth: "180px" } },
        muiTableBodyCellProps: { sx: { width: "180px", maxWidth: "180px" } },
        Cell: ({ row }) => (
          <Typography variant="body14" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {row.original.internalNote || EMPTY_STR.TEXT}
          </Typography>
        ),
      },
      {
        header: "最終更新日",
        accessorKey: "updatedAt",
        size: 106,
        muiTableHeadCellProps: { sx: { width: "106px" } },
        muiTableBodyCellProps: { sx: { width: "106px" } },
        Cell: ({ row }) => {
          const date = row.original.updatedAt;
          return <Typography variant="body14">{date ? dayjs(date).format(DATE_FORMAT) : EMPTY_STR.TEXT}</Typography>;
        },
      },
    ],
    [],
  );

  const { table } = useTable({
    columns,
    rows: rows ?? [],
    onClickRow: handleRowClick,
    enableRowOrdering: true,
    enableSorting: false,
    displayColumnDefOptions: {
      "mrt-row-drag": {
        header: "",
        size: 56,
      },
    },
    muiRowDragHandleProps: ({ table: tableInstance }) => ({
      onDragEnd: () => {
        const { draggingRow, hoveredRow } = tableInstance.getState();
        if (draggingRow && hoveredRow) {
          onDragEnd(draggingRow, hoveredRow as MRT_Row<TLearningContentCategory>);
        }
      },
    }),
  });

  return <Table table={table} isLoading={isLoading} isFetching={isFetching} />;
};
